import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/http';
import { mutationKeys, queryKeys } from '@/api/queryKeys';

/**
 * Whether a key is **the** exempt key, and not merely something that starts like it.
 *
 * ⚠️ Both exemptions below compared element `[0]` alone, so every key beginning `['session', …]` or
 * `['sign-in', …]` was exempt too — a future `['sign-in', 'retry']` would have stepped over the
 * sign-out rule silently, and nothing pinned the narrowness the two docblocks claim. An exemption
 * that widens by itself as keys are added is not an exemption, it is a prefix match.
 */
function isKey(key: readonly unknown[] | undefined, exempt: readonly unknown[]): boolean {
  return (
    key !== undefined &&
    key.length === exempt.length &&
    key.every((part, index) => part === exempt[index])
  );
}

/**
 * **The one routine every change of reader runs, so the list of what must happen is in one place.**
 *
 * There are four doors out of a session and no more: signing in, signing out, redeeming an
 * invitation, and the session simply ending (a 401 — the door nobody presses). Each of them used to
 * be wired by hand, and that is exactly how BUG-045's fourth door came to be missed and then
 * BUG-066 beside it: the *second* thing that has to happen on a change of reader lived in a helper
 * of sign-out's own (`forgetChartMeasurements`, now folded in below), so a session that expired left
 * the departed reader's last axis label in the DOM of the sign-in page. A third such item would have
 * been forgotten the same way. So the doors no longer decide *what* happens — they call this, and
 * anything the next item requires is added here once.
 *
 * ⚠️ **`emptyTheCache` is the one difference between the doors, and it is the behaviour each of them
 * already had.** The three deliberate doors empty the cache because everything in it belongs to
 * whoever was signed in before — candidate names, a blind-filtered dashboard (ADR 0015) — and on a
 * shared machine the next person's first screen would be drawn from it. The 401 door never did, and
 * BUG-066 did not widen it: it is reached from the **session query's own error handler**, while
 * `RequireSession` is still observing that query, and `removeQueries` on an observed entry orphans
 * the observer rather than refreshing it — the mechanism this file records at length for branding,
 * pointed at the one query the redirect depends on. What makes the narrower door sound is that its
 * screens unmount into `/sign-in` on the same tick and the next reader's **sign-in** passes through
 * here with `emptyTheCache: true`, so nothing cached survives to be read by them.
 *
 * What follows is the rest of the list, and it applies at **every** door.
 *
 * ⚠️ **The brand is put back in question rather than dropped**, which is the half a plain `clear()`
 * cannot do.
 *
 * ⚠️ **`GET /api/v1/branding/` answers the session's tenant now** (BUG-045, backend 2026-08-09,
 * option B): it returns `request.user.tenant` when there is a cookie and falls back to the host
 * only when there is not. So the brand is no longer a property of the tab, it is a property of
 * whoever is signed in — while `TenantBrand` and `TenantMark` hold it at `staleTime: Infinity`.
 * Without what follows, one reader's name, accent and logo render to the next in the same tab: a
 * cross-tenant mix-up on the surface a white-label product exists for.
 *
 * ⚠️ **Branding is deliberately *not* swept up with the rest, and that is the crux.**
 * `queryClient.clear()` removes the cache entry, and a mounted observer whose entry has been
 * removed is **orphaned**: it keeps rendering its last result and never refetches, because nothing
 * notifies it and nothing re-renders it. `useSignOut` records the same finding from a live browser
 * — *"`clear()` drops the entries but does not make a mounted query refetch"*. Every other screen
 * survives that because it unmounts on the way out; `TenantBrand` mounts **above the router**
 * (`App.tsx`) precisely so `/sign-in` is branded too, so it is the one observer that outlives the
 * change of reader. Sweeping its entry away and then invalidating what is no longer there is a
 * no-op — measured: the sidebar read `Acme Inc` while `--accent` and `--logo` were still the
 * previous brand's.
 *
 * So branding is **reset in place**: `resetQueries` empties the entry *and* refetches it through
 * the observers that are still attached. Emptying rather than merely invalidating is what keeps
 * the previous brand off the screen during the request — with no data, `AppShell` falls back to
 * the session's own tenant name and `TenantBrand` hands the page back to the product's colour, so
 * the worst intermediate state is the product's own, never another organisation's.
 *
 * The mutation cache goes too, which is what `clear()` did and what this replaces.
 *
 * ⚠️ **And the chart measurement span goes, at every door** (BUG-066). Recharts measures text by
 * writing it into one absolutely-positioned `<span>` it appends directly to `<body>` and never
 * removes, so the last string it sized survives every unmount — after signing out,
 * `document.body.innerText` on the sign-in screen still ended with `STRONG YES`. It is an axis label
 * rather than anybody's feedback, so this is hygiene and not an ADR 0006 breach; but a fragment of
 * the previous session's charts has no business in the DOM of an unauthenticated page, and that
 * holds at the door nobody presses exactly as it does at the button. Recharts looks the element up
 * by id on every measurement and recreates it when it is gone (`recharts/lib/util/DOMUtils`), so
 * removing it costs one measurement and nothing else.
 */
export function readerChanged(
  client: QueryClient,
  { emptyTheCache }: { emptyTheCache: boolean },
): void {
  if (emptyTheCache) {
    client.removeQueries({
      predicate: (query) => !isKey(query.queryKey, queryKeys.branding()),
    });
    client.getMutationCache().clear();
  }
  void client.resetQueries({ queryKey: queryKeys.branding() });
  document.getElementById('recharts_measurement_span')?.remove();
}

/**
 * **The fourth exit, and the only one nobody presses: the session simply ends.**
 *
 * `readerChanged` is called outright from the three *deliberate* changes of reader — sign-in,
 * sign-out and redeeming an invitation. A cookie that expires, or a sign-out performed in another
 * tab, goes through none of them: a 401 arrives, `RequireSession` renders `<Navigate to="/sign-in">`,
 * and no cache work happens at all. Before BUG-045 that was correct, because the cached entry was the
 * **host's** brand and leaving it on the sign-in screen was the whole point. Since BUG-045 the entry
 * may be the departed reader's own tenant, and `TenantBrand` is mounted above the router at
 * `staleTime: Infinity` and does not unmount — so their `--accent`, their `--logo` and their name in
 * `TenantMark`'s `alt` stay applied to the sign-in screen the **next person at that machine** sees.
 * That is the cross-tenant mix-up the other three doors were closed against, one door over.
 *
 * ⚠️ **Anchored on the session query's own 401, which is where every door leads.** A 401 from any
 * other query or mutation invalidates the session, the session refetches, and the cookie being gone
 * it answers 401 too — so this runs once, in the same tick as the redirect, rather than once per
 * failing request in a screenful of them.
 *
 * ⚠️ **`hadReader` is what keeps a cold sign-in page alone.** `GET /api/v1/auth/me/` answering 401
 * with **no data behind it** is the ordinary "nobody is signed in" reply of a first load, where the
 * brand in the cache was fetched anonymously and is already the host's; emptying it there would blank
 * the mark and spend a second request to fetch the same answer. Data in the entry means there *was* a
 * reader, and it is theirs that is on screen.
 *
 * ⚠️ **It guards the whole routine, not the brand alone** (BUG-066). Nothing else in the list is
 * expensive — a chart span a reader who never had a session cannot have left behind — but a door
 * that fires when nobody went through it is a door that will accumulate work nobody meant, and the
 * cheapest place to say *"no reader changed here"* is once, at the top.
 *
 * The cache is deliberately **not** emptied here and the brand is reset rather than removed; both
 * reasons are `readerChanged`'s own, and both are about the observers that outlive this transition.
 */
function sessionEnded(client: QueryClient, hadReader: boolean): void {
  if (!hadReader) return;
  readerChanged(client, { emptyTheCache: false });
}

/**
 * Server state lives here and nowhere else — never copied into Zustand or Jotai, which would
 * create two sources of truth that diverge (`.claude/rules/frontend.md`).
 */
export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    /**
     * A 401 anywhere means the session ended — the cookie expired, or someone signed out in
     * another tab. Rather than each screen inventing its own handling, the session query is
     * marked stale here, `RequireSession` sees it answer **401** and sends the user to sign-in with
     * the address they were on. This is the behaviour BUG-013 recorded and deferred to the sprint
     * that builds sign-in.
     *
     * ⚠️ **It reads the status and not merely the failure, since BUG-103**, and this sentence said
     * *"sees it fail"* until then, which is what the gate did: every way `me/` could fail became
     * *"your session ended"*, so a stopped backend answering 502 sent a reader with a live session
     * to sign-in. Only the 401 belongs to this paragraph.
     *
     * ⚠️ **And it is the fourth door of `readerChanged`**, which it was not until BUG-045's fourth
     * door was found and BUG-066 beside it: see `sessionEnded` above.
     */
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!(error instanceof ApiError) || !error.isUnauthenticated) return;
        // ⚠️ Except when the 401 IS the session query's own answer. `GET /api/v1/auth/me/`
        // answering 401 is the ordinary "nobody is signed in" reply, not an ended session;
        // invalidating it here refetches it, it fails again, and the handler runs again — an
        // unbounded loop that hangs the test suite and, in a browser, would hammer a route
        // throttled at 300/min per user (BUG-011) from the sign-in screen.
        if (isKey(query.queryKey, queryKeys.session())) {
          sessionEnded(client, query.state.data !== undefined);
          return;
        }
        void client.invalidateQueries({ queryKey: queryKeys.session() });
      },
    }),
    /**
     * The other half of the same rule: TASK-056 says **a 401 from _any_ request**, and a cache that
     * watched queries only would let a write die quietly. The next sprint's draft autosave is a
     * `PATCH` (ADR 0007), and an interviewer typing into a dead session is the exact loss that ADR
     * exists to prevent: without this, the save fails, nothing on screen changes, and the work is
     * gone the moment the tab closes.
     *
     * ⚠️ **The login mutation is exempt** (`mutationKeys.signIn`), and it alone — `isKey` compares
     * the whole key, so a later `['sign-in', …]` is not quietly exempt as well. Its 401 is "wrong
     * password", not "your session ended" — routing it here would invalidate a session that does not
     * exist and clear the very screen that has to show the message.
     */
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (!(error instanceof ApiError) || !error.isUnauthenticated) return;
        if (isKey(mutation.options.mutationKey, mutationKeys.signIn())) return;
        void client.invalidateQueries({ queryKey: queryKeys.session() });
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // Retrying an answer the server meant is noise: a refused, missing or throttled request
          // will be refused, missing or throttled again. A throttle in particular must not be
          // amplified by the client that caused it (BUG-005, BUG-011).
          if (error instanceof ApiError) {
            if (error.status === 0) return failureCount < 2;
            return false;
          }
          return failureCount < 1;
        },
      },
      mutations: { retry: false },
    },
  });

  return client;
}
